import React from 'react';
import { Box, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Button, CircularProgress } from '@mui/material';
import { BuildOutlined, AddCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../../app/hooks';
import { SectionHeaderWithCommand, SectionPaper } from '../../../../common/components/styled-ui-elements';
import { useGetAssetRecurringTasksQuery } from '../../../../../services/AssetService/assetApi';

/**
 * MaintenanceScheduleSection - Displays read-only list of PM recurring tasks linked to this asset.
 * Only shown on the edit page (requires an existing asset ID).
 */
export const MaintenanceScheduleSection: React.FC = () => {
  const asset = useAppSelector((state) => state.asset.currentAsset);
  const assetId = asset?.id;

  const { data: recurringTasks, isLoading, isError } = useGetAssetRecurringTasksQuery(
    assetId ?? '',
    { skip: !assetId }
  );

  const navigate = useNavigate();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRIT': return 'error';
      case 'HIGH': return 'warning';
      case 'MED': return 'info';
      case 'LOW': return 'success';
      case 'PLAN': return 'default';
      default: return 'default';
    }
  };

  const getScheduleLabel = (task: { schedule?: { type: string; interval: number } }) => {
    if (!task.schedule) return '—';
    const { type, interval } = task.schedule;
    if (interval === 1) {
      switch (type) {
        case 'DAILY': return 'Daily';
        case 'WEEKLY': return 'Weekly';
        case 'MONTHLY': return 'Monthly';
        default: return type;
      }
    }
    switch (type) {
      case 'DAILY': return `Every ${interval} days`;
      case 'WEEKLY': return `Every ${interval} weeks`;
      case 'MONTHLY': return `Every ${interval} months`;
      default: return `${type} / ${interval}`;
    }
  };

  const handleCreateRecurringTask = () => {
    if (!asset) return;
    const params = new URLSearchParams({
      category: 'PM',
      assetId: asset.id,
      assetTag: asset.assetTag,
      assetName: asset.name,
      centerId: asset.center,
    });
    navigate(`/recurring-tasks/create?${params.toString()}`);
  };

  if (!assetId) return null;

  return (
    <Box>
      <SectionHeaderWithCommand
        icon={<BuildOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title="Preventive Maintenance"
        command={
          <Button
            size="small"
            startIcon={<AddCircleOutline />}
            onClick={handleCreateRecurringTask}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Create PM Recurring Task
          </Button>
        }
      />
      <SectionPaper>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        )}
        {isError && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            Failed to load recurring tasks.
          </Typography>
        )}
        {!isLoading && !isError && (!recurringTasks || recurringTasks.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No preventive maintenance recurring tasks linked to this asset.
          </Typography>
        )}
        {!isLoading && !isError && recurringTasks && recurringTasks.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Schedule</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>Next Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }} align="right">Tickets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recurringTasks.map((rt) => (
                  <TableRow
                    key={rt.id}
                    hover
                    onClick={() => navigate(`/recurring-tasks/edit?id=${rt.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f0f9ff' } }}
                  >
                    <TableCell>{rt.templateTitle}</TableCell>
                    <TableCell>{getScheduleLabel(rt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={rt.priority}
                        size="small"
                        color={getPriorityColor(rt.priority) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rt.isActive ? 'Active' : 'Paused'}
                        size="small"
                        color={rt.isActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {rt.nextScheduledDate
                        ? new Date(rt.nextScheduledDate).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{rt.generatedTicketCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionPaper>
    </Box>
  );
};

export default MaintenanceScheduleSection;
